import { InlineAddress } from 'types';
import {
  formatOneLineAddressWithoutCountry,
  formatAddressSecondLine,
  formatAddressFirstLine,
} from 'utils/formatters';

type AutocompletePrediction = google.maps.places.AutocompletePrediction;
type PlaceResult = google.maps.places.PlaceResult;
type AddressComponent = google.maps.GeocoderAddressComponent;

// ---------------------------------------------------------------------------
// Script loader + services singleton
// ---------------------------------------------------------------------------

export interface GoogleServices {
  autocomplete: google.maps.places.AutocompleteService;
  places: google.maps.places.PlacesService;
}

const SCRIPT_ID = 'google-maps';
const SCRIPT_URL = (apiKey: string): string =>
  `https://maps.googleapis.com/maps/api/js?key=${apiKey}&libraries=places`;

let services: GoogleServices | null = null;

// Injects the Google Maps + Places script tag exactly once
export const ensureGoogleMapsScriptTag = (apiKey: string): void => {
  if (
    typeof window === 'undefined' ||
    document.querySelector(`#${SCRIPT_ID}`)
  ) {
    return;
  }

  const script = document.createElement('script');
  script.async = true;
  script.id = SCRIPT_ID;
  script.src = SCRIPT_URL(apiKey);
  document.head?.appendChild(script);
};

export const getGoogleServicesIfReady = (
  hostElement: HTMLElement | null
): GoogleServices | null => {
  if (services) {
    return services;
  }

  if (!window.google?.maps?.places) {
    return null;
  }

  const googlePlaces = window.google.maps.places;

  const attributionNode =
    hostElement?.appendChild(document.createElement('div')) ??
    document.createElement('div');

  services = {
    autocomplete: new googlePlaces.AutocompleteService(),
    places: new googlePlaces.PlacesService(attributionNode),
  };

  return services;
};

export const __resetGoogleServicesForTesting = (): void => {
  services = null;
};

const GOOGLE_TO_INLINEADDRESS_MAPPING: Record<string, keyof InlineAddress> = {
  subpremise: 'unitNumber',
  street_number: 'streetNumber',
  route: 'streetName',
  locality: 'suburb',
  postal_code: 'postcode',
  administrative_area_level_1: 'state',
};

export const parseGoogleAttribute = (
  type: string | undefined
): keyof InlineAddress | null => {
  if (!type) {
    return null;
  }

  return GOOGLE_TO_INLINEADDRESS_MAPPING[type] ?? null;
};

export const mapPlaceResultToInlineAddress = (
  details: PlaceResult
): InlineAddress => {
  const components: AddressComponent[] = details?.address_components ?? [];
  const address: InlineAddress = {};

  components.forEach((component) => {
    const primaryType = component.types[0];
    const field = parseGoogleAttribute(primaryType);
    if (!field) {
      return;
    }

    // For state, we want short_name (NSW) instead of long_name (New South Wales). Use long_name for other fields
    address[field] =
      primaryType === 'administrative_area_level_1'
        ? component.short_name
        : component.long_name;
  });

  return address;
};

export const REQUIRED_ADDRESS_FIELDS = [
  'streetNumber',
  'streetName',
  'suburb',
  'state',
  'postcode',
] as const;

export const hasCompleteAddress = (address: InlineAddress): boolean => {
  return REQUIRED_ADDRESS_FIELDS.every((field) => Boolean(address[field]));
};

// ---------------------------------------------------------------------------
// Prediction helpers (used to prefill MUI Autocomplete state)
// ---------------------------------------------------------------------------

export const EMPTY_PREDICTION: AutocompletePrediction = {
  description: '',
  matched_substrings: [],
  place_id: '',
  structured_formatting:
    null as unknown as AutocompletePrediction['structured_formatting'],
  terms: [],
  types: [],
};

export const makePredictionFromDescription = (
  description: string
): AutocompletePrediction => ({
  ...EMPTY_PREDICTION,
  description,
});

/**
 * Builds an `AutocompletePrediction` from a manually-entered address so the
 * MUI Autocomplete can display a properly-formatted structured entry
 * (main line = street, secondary line = suburb/state/postcode).
 */
export const buildPredictionFromManualAddress = (
  address: InlineAddress
): AutocompletePrediction => ({
  ...EMPTY_PREDICTION,
  description: formatOneLineAddressWithoutCountry(address),
  structured_formatting: {
    main_text: formatAddressFirstLine(address),
    secondary_text: formatAddressSecondLine(address),
    main_text_matched_substrings: [{ offset: 0, length: 0 }],
  },
});
